import { useCallback, useMemo } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	getValuationSummaryResult,
	startCalculateValuation,
	streamCalculateValuation,
	ValuationComputeFailedError,
} from '@dealstack/frontend/shared/dataExchange/services/valuations.service';
import type { ValuationComputeProgressRes } from '@dealstack/shared/lib/generated';
import { NEW_VALUATION_ID } from '@dealstack/frontend/app/pages/Valuations/shared/valuationRoutes';
import {
	VALUATION_COMPUTE_PROGRESS_QUERY_KEY,
	VALUATION_COMPUTE_QUERY_KEY,
	VALUATION_COMPUTE_START_QUERY_KEY,
	VALUATION_SUMMARY_RESULT_QUERY_KEY,
	VALUATIONS_QUERY_KEY,
} from '@dealstack/frontend/app/pages/Valuations/queries/queryKeys';
import { computeValidationErrors } from '@dealstack/frontend/app/pages/Valuations/queries/validationErrors';

const COMPUTE_CACHE_TIME_MS = 5 * 60 * 1000;
const COMPUTE_STREAM_RETRIES = 2;

export function useCalculationWorkflow(
	dealId: string | undefined,
	valuationId: string | undefined
) {
	const queryClient = useQueryClient();

	const isComputeEnabled = !!dealId && !!valuationId && valuationId !== NEW_VALUATION_ID;
	const startKey = [VALUATION_COMPUTE_START_QUERY_KEY, dealId ?? '', valuationId ?? ''];
	const progressKey = [VALUATION_COMPUTE_PROGRESS_QUERY_KEY, dealId ?? '', valuationId ?? ''];

	// Start the compute (Temporal workflow) as a cached query keyed on deal+valuation, so the cache
	// outlives a component mount and dedupes: navigating between sibling views reuses the same
	// workflowId instead of starting a second compute, and StrictMode's double mount no longer
	// doubles it. An input or valuation edit (or `reloadSummary`) refetches this key to start a fresh
	// workflow. WAT-730.
	const startQuery = useQuery({
		queryKey: startKey,
		queryFn: () =>
			startCalculateValuation({ valuationId: valuationId ?? '', dealId: dealId ?? '' }),
		enabled: isComputeEnabled,
		staleTime: Infinity,
		gcTime: COMPUTE_CACHE_TIME_MS,
		retry: false,
	});

	const workflowId = startQuery.data?.workflowId;
	// Key the stream on the workflowId so a fresh start (new workflowId) supersedes the previous
	// stream instead of reusing its cached result.
	const computeKey = [
		VALUATION_COMPUTE_QUERY_KEY,
		dealId ?? '',
		valuationId ?? '',
		workflowId ?? '',
	];

	// Relay the running compute's progress + terminal result over SSE. Resolves with { resultId }.
	const computeQuery = useQuery({
		queryKey: computeKey,
		queryFn: ({ signal }) =>
			streamCalculateValuation({
				valuationId: valuationId ?? '',
				dealId: dealId ?? '',
				workflowId: workflowId ?? '',
				// Fan progress out through the cache so whichever mount renders the loader sees it,
				// not just the (possibly already unmounted) mount whose queryFn ran the stream.
				onProgress: (progress) => queryClient.setQueryData(progressKey, progress),
				// React Query aborts this signal on supersede/unmount/refetch; forwarding it tears down
				// the SSE fetch (NOT the compute - Temporal owns that) instead of leaving a zombie
				// reader. WAT-730.
				signal,
			}).finally(() => queryClient.setQueryData(progressKey, null)),
		enabled: isComputeEnabled && !!workflowId,
		staleTime: Infinity,
		gcTime: COMPUTE_CACHE_TIME_MS,
		// A stream that dies before its terminal event leaves resultId unset, which disables the
		// result query below - so a single dropped connection strands a compute that has already
		// succeeded, with nothing on screen to recover it. Reconnecting is safe and cheap by design:
		// the relay replays the Redis stream from the start, so a reopened stream re-delivers the
		// result immediately when the compute has already finished. That replay is also why a failed
		// compute is not retried: it would re-deliver the same error. Cancellations (unmount,
		// supersede) are not errors and are not retried either. WAT-730.
		retry: (failureCount, error) =>
			failureCount < COMPUTE_STREAM_RETRIES &&
			!(error instanceof ValuationComputeFailedError),
	});

	const resultId = computeQuery.data?.resultId;

	const resultQuery = useQuery({
		queryKey: [
			VALUATION_SUMMARY_RESULT_QUERY_KEY,
			dealId ?? '',
			valuationId ?? '',
			resultId ?? '',
		],
		queryFn: () =>
			getValuationSummaryResult({
				dealId: dealId ?? '',
				valuationId: valuationId ?? '',
				resultId: resultId ?? '',
			}),
		enabled: !!resultId,
		staleTime: Infinity,
		gcTime: COMPUTE_CACHE_TIME_MS,
		placeholderData: keepPreviousData,
		retry: false,
	});

	// Progress is only ever written via setQueryData from the compute stream above; this observer
	// just subscribes to that cache entry, so it never fetches on its own.
	const { data: computeProgress } = useQuery<ValuationComputeProgressRes | null>({
		queryKey: progressKey,
		queryFn: () => null,
		enabled: false,
		initialData: null,
	});

	const summaryByPerson = resultQuery.data?.payload?.result?.byPerson;
	const summaryByMip = resultQuery.data?.payload?.result?.byMip;
	const validationErrors = useMemo(
		() => computeValidationErrors(summaryByPerson),
		[summaryByPerson]
	);

	// Force a fresh compute (e.g. after a person-input change): refetch the start query to mint a new
	// workflowId, which cascades into a new stream + result, so every mounted view picks up the new
	// result.
	const reloadSummary = useCallback(
		() => queryClient.refetchQueries({ queryKey: startKey }),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[queryClient, dealId, valuationId]
	);

	const invalidateValuations = useCallback(() => {
		if (dealId) {
			void queryClient.invalidateQueries({ queryKey: [VALUATIONS_QUERY_KEY, dealId] });
		}
	}, [dealId, queryClient]);

	return {
		reloadSummary,
		isSummaryFirstLoad:
			!resultQuery.data &&
			(startQuery.isLoading || computeQuery.isLoading || resultQuery.isLoading),
		isSummaryLoading:
			startQuery.isFetching || computeQuery.isFetching || resultQuery.isFetching,
		// Surface start/compute/result failure so the view can show an in-page error + retry instead
		// of a blank summary; the global QueryCache.onError still fires the toast. WAT-730.
		isSummaryError: startQuery.isError || computeQuery.isError || resultQuery.isError,
		computeProgress,
		summaryByPerson,
		summaryByMip,
		validationErrors,
		resultId,
		linearSolve: computeQuery.data?.linearSolve,
		invalidateValuations,
	};
}
